import { useState, type ReactNode } from 'react';
import { Check, MapPin, PenLine, ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/atoms/button';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/popover';

interface AddressOption {
  street?: string;
  street2?: string | null;
  city?: string;
  state?: string;
  zip?: string;
  isPrimary?: boolean;
}

interface AddressSelectorProps<TAddress extends AddressOption> {
  title: string;
  mode: 'select' | 'manual';
  addresses: TAddress[];
  visibleAddresses: TAddress[];
  selectedIndex: number | null;
  contactSelected: boolean;
  showAll: boolean;
  maxVisibleAddresses: number;
  emptyContactMessage: string;
  emptyAddressMessage: string;
  onToggleMode: () => void;
  onSelectAddress: (index: number, address: TAddress) => void;
  onToggleShowAll: () => void;
  renderManual: ReactNode;
  showModeToggle?: boolean;
  /**
   * When true, manual entry is presented as an option in the address source
   * picker instead of a separate header action.
   */
  manualOptionInList?: boolean;
  /** When true, fill the parent column's height so sibling cards stay equal-height. */
  fillHeight?: boolean;
}

function formatAddressLine(address: AddressOption) {
  return (
    [address.city, address.state, address.zip].filter(Boolean).join(', ') || 'Incomplete address'
  );
}

function formatStreetLine(address: AddressOption) {
  return [address.street, address.street2].filter(Boolean).join(', ') || 'No street';
}

export function AddressSelector<TAddress extends AddressOption>({
  title,
  mode,
  addresses,
  selectedIndex,
  contactSelected,
  emptyContactMessage,
  emptyAddressMessage,
  onToggleMode,
  onSelectAddress,
  renderManual,
  showModeToggle = true,
  manualOptionInList = false,
  fillHeight = false,
}: AddressSelectorProps<TAddress>) {
  const [popoverOpen, setPopoverOpen] = useState(false);

  const selectedAddress = selectedIndex !== null ? addresses[selectedIndex] : null;
  const hasMultiple = addresses.length > 1;
  const hasManualListOption = showModeToggle && manualOptionInList;
  const sourcePickerEnabled =
    mode === 'manual' ? hasManualListOption : hasMultiple || hasManualListOption;
  const showSourcePickerArea =
    mode === 'manual' ? hasManualListOption : mode === 'select' && addresses.length > 0;
  const cardFillClass = fillHeight ? ' flex-1' : '';
  const sourceTitle =
    mode === 'manual'
      ? 'Enter manually'
      : selectedAddress
        ? formatStreetLine(selectedAddress)
        : 'Select an address';
  const sourceSub =
    mode === 'manual'
      ? 'Use a one-off address'
      : selectedAddress
        ? formatAddressLine(selectedAddress)
        : 'Choose from this contact';

  return (
    <div className={fillHeight ? 'flex h-full flex-col gap-3' : 'space-y-3'}>
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold">{title}</p>
        {showModeToggle && !manualOptionInList && addresses.length > 0 && (
          <Button variant="ghost" size="sm" className="text-xs" onClick={onToggleMode}>
            {mode === 'manual' ? (
              <>
                <MapPin className="mr-1 h-3 w-3" /> Select from contact
              </>
            ) : (
              <>
                <PenLine className="mr-1 h-3 w-3" /> Enter manually
              </>
            )}
          </Button>
        )}
      </div>

      {showSourcePickerArea && (
        <>
          {sourcePickerEnabled ? (
            <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className={`relative flex w-full cursor-pointer items-start gap-3 rounded-lg border border-border bg-muted/40 p-3 text-left text-sm transition-colors hover:bg-muted/70${cardFillClass}`}
                >
                  {mode === 'manual' ? (
                    <PenLine className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  ) : (
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{sourceTitle}</p>
                    <p className="text-xs text-muted-foreground">{sourceSub}</p>
                    {mode === 'select' && selectedAddress?.isPrimary && (
                      <span className="mt-1 inline-block rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
                        Primary
                      </span>
                    )}
                  </div>
                  <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-2" align="start">
                <div className="space-y-1">
                  {addresses.map((address, index) => {
                    const isSelected = mode === 'select' && selectedIndex === index;
                    return (
                      <button
                        key={index}
                        type="button"
                        onClick={() => {
                          onSelectAddress(index, address);
                          setPopoverOpen(false);
                        }}
                        className={`flex w-full items-start gap-3 rounded-md p-2.5 text-left text-sm transition-colors ${
                          isSelected ? 'bg-primary/10 text-primary' : 'hover:bg-muted'
                        }`}
                      >
                        <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-medium">{formatStreetLine(address)}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatAddressLine(address)}
                          </p>
                          {address.isPrimary && (
                            <span className="mt-0.5 inline-block rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
                              Primary
                            </span>
                          )}
                        </div>
                        {isSelected && <Check className="h-3.5 w-3.5 shrink-0" />}
                      </button>
                    );
                  })}
                  {hasManualListOption ? (
                    <button
                      type="button"
                      onClick={() => {
                        if (mode !== 'manual') onToggleMode();
                        setPopoverOpen(false);
                      }}
                      className={`flex w-full items-start gap-3 rounded-md p-2.5 text-left text-sm transition-colors ${
                        mode === 'manual' ? 'bg-primary/10 text-primary' : 'hover:bg-muted'
                      }`}
                    >
                      <PenLine className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium">Enter manually</p>
                        <p className="text-xs text-muted-foreground">Use a one-off address</p>
                      </div>
                      {mode === 'manual' && <Check className="h-3.5 w-3.5 shrink-0" />}
                    </button>
                  ) : null}
                </div>
              </PopoverContent>
            </Popover>
          ) : null}

          {mode === 'select' && !sourcePickerEnabled && selectedAddress && (
            <button
              type="button"
              disabled
              className={`relative flex w-full cursor-default items-start gap-3 rounded-lg border border-border bg-muted/40 p-3 text-left text-sm${cardFillClass}`}
            >
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{formatStreetLine(selectedAddress)}</p>
                <p className="text-xs text-muted-foreground">
                  {formatAddressLine(selectedAddress)}
                </p>
                {selectedAddress.isPrimary && (
                  <span className="mt-1 inline-block rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
                    Primary
                  </span>
                )}
              </div>
              <Check className="h-4 w-4 shrink-0 text-primary" />
            </button>
          )}

          {mode === 'select' && !sourcePickerEnabled && !selectedAddress && !hasMultiple && (
            <button
              type="button"
              onClick={() => onSelectAddress(0, addresses[0])}
              className={`relative flex w-full items-start gap-3 rounded-lg border border-border p-3 text-left text-sm transition-colors hover:border-muted-foreground/30 hover:bg-muted/50${cardFillClass}`}
            >
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{formatStreetLine(addresses[0])}</p>
                <p className="text-xs text-muted-foreground">{formatAddressLine(addresses[0])}</p>
                {addresses[0].isPrimary && (
                  <span className="mt-1 inline-block rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
                    Primary
                  </span>
                )}
              </div>
            </button>
          )}
        </>
      )}

      {mode === 'select' && addresses.length === 0 && !contactSelected && (
        <p className="text-sm text-muted-foreground">{emptyContactMessage}</p>
      )}

      {mode === 'select' && addresses.length === 0 && contactSelected && (
        <div className={showModeToggle ? 'py-4 text-center' : ''}>
          <p className={`text-sm text-muted-foreground${showModeToggle ? ' mb-2' : ''}`}>
            {emptyAddressMessage}
          </p>
          {showModeToggle && manualOptionInList ? (
            <button
              type="button"
              onClick={onToggleMode}
              className="mx-auto flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm transition-colors hover:bg-muted/60"
            >
              <PenLine className="h-3.5 w-3.5" /> Enter manually
            </button>
          ) : showModeToggle ? (
            <Button variant="outline" size="sm" onClick={onToggleMode}>
              <PenLine className="mr-1.5 h-3.5 w-3.5" /> Enter manually
            </Button>
          ) : null}
        </div>
      )}

      {mode === 'manual' && renderManual}
    </div>
  );
}
